import { request } from '@/common/appCtx';
import { MeetTarget } from '@/pages/workstation-dashboard/const';

export interface GetGroupThroughputOverviewParams {
  activity_type_list?: number[];
  workstation_group_id_list?: number[];
}

export interface GetGroupThroughputOverviewResponse {
  retcode?: number;
  message?: string;
  data?: {
    calculate_method?: number;
    list?: {
      interactive_type_throughput_list?: {
        activity_type?: number;
        is_calculate_type?: boolean;
        throughput?: number;
      }[];
      non_interactive_type_throughput_list?: {
        activity_type?: number;
        is_calculate_type?: boolean;
        throughput?: number;
      }[];
      total_active_ops_count?: number;
      total_active_workstation_count?: number;
      total_throughput?: number;
      workstation_group?: string;
      workstation_group_id?: number;
      workstation_group_name?: string;
    }[];
    time_begin?: number;
    time_end?: number;
  };
}

// Group Throughput Overview
export const getGroupThroughputOverview = (params: GetGroupThroughputOverviewParams = {}) => {
  return request.post<GetGroupThroughputOverviewResponse>('/api/wfm/admin/workstation/dashboard/dashboard_group_last_hour', params);
};

export enum TimeRangeType {
  LastHour,
  Last10Hours,
}

export interface GetGroupDropdownListRequest {
  time_range_type?: TimeRangeType;
}

export interface GetGroupDropdownListResponse {
  data?: {
    list?: {
      workstation_group?: string;
      workstation_group_id?: number;
      workstation_group_name?: string;
    }[];
  };
  message?: string;
  retcode?: number;
}

export const getGroupDropdownList = (params: GetGroupDropdownListRequest) => {
  return request.post<GetGroupDropdownListResponse>('/api/wfm/admin/workstation/dashboard/dashboard_group_dropdown', params);
};

export interface GetGroupLastTenHoursParams {
  workstation_group_id_list?: number[];
}

export interface GetGroupLastTenHoursResponse {
  retcode?: number;
  message?: string;
  data?: {
    list?: {
      item_list?: {
        throughput?: number;
        time_begin?: number;
        time_end?: number;
      }[];
      // workstation_group_id?: string;
      // workstation_group_name?: string;
      workstation_group?: string;
    }[];
  };
}

// Last 10 Hours Group Throughput
export const getGroupLastTenHoursThroughput = (params: GetGroupLastTenHoursParams = {}) => {
  return request.post<GetGroupLastTenHoursResponse>('/api/wfm/admin/workstation/dashboard/dashboard_group_last_ten_hours', params);
};

export interface GetWorkstationPerformanceLastHourParams {
  count?: number;
  pageno?: number;
  /**
   * 0: all, 1: on target, 2: below target
   */
  select_meet_target?: number;
  workstation_group_id: number;
  // 0: none, 10/11: (Hourly Productivity) asc/desc, 20/21: (Manhour) asc/desc, 30/31: (Throughput) asc/desc,
  order_by_type?: number;
  biz_workstation_id_list?: string[];
}

export interface GetWorkstationPerformanceLastHourResponse {
  retcode?: number;
  message?: string;
  data?: {
    count?: number;
    items?: {
      hourly_productivity?: number;
      manhour?: number;
      manpower_check_in?: number;
      target?: number;
      throughput?: number;
      workstation?: string;
    }[];
    pageno?: number;
    total?: number;
    total_info?: {
      below_target_count?: number;
      on_target_count?: number;
      total_count?: number;
    };
  };
}

// Workstation Performance Last Hour
export const getWorkstationPerformanceLastHour= (params: GetWorkstationPerformanceLastHourParams) => {
  return request.post<GetWorkstationPerformanceLastHourResponse>('/api/wfm/admin/workstation/dashboard/dashboard_workstation_last_hour', params);
};

export interface GetWorkstationDropdownListParams {
  time_range_type?: TimeRangeType;
  workstation_group_id?: number;
}

export interface GetWorkstationDropdownListResponse {
  data?: {
    list?: {
      biz_workstation_id?: string;
      workstation_group_name?: string;
    }[];
  };
  message?: string;
  retcode?: number;
}

export const getWorkstationDropdownList = (params: GetWorkstationDropdownListParams) => {
  return request.post<GetWorkstationDropdownListResponse>('/api/wfm/admin/workstation/dashboard/dashboard_workstation_dropdown', params);
};

export interface GetOperatorPerformanceLastHourParams {
  biz_workstation_id_list?: string[];
  count?: number;
  operator_id_list?: string[];
  /**
   * 0: none, 10/11: (Hourly Productivity) asc/desc, 20/21: (Manhour) asc/desc, 30/31: (Throughput) asc/desc,
   */
  order_by_type?: number;
  pageno?: number;
  /**
   * 0: all, 1: on target, 2: below target
   */
  select_meet_target?: MeetTarget;
  workstation_group_id?: number;
}

export interface GetOperatorPerformanceLastHourResponse {
  retcode?: number;
  message?: string;
  data?: {
    count?: number;
    items?: {
      hourly_productivity?: number;
      manhour?: number;
      operator?: string;
      target?: number;
      throughput?: number;
      workstation?: string;
    }[];
    pageno?: number;
    total?: number;
    total_info?: {
      below_target_count?: number;
      on_target_count?: number;
      total_count?: number;
    };
  };
}

export const getOperatorPerformanceLastHour = (params: GetOperatorPerformanceLastHourParams) => {
  return request.post<GetOperatorPerformanceLastHourResponse>('/api/wfm/admin/workstation/dashboard/dashboard_operator_last_hour', params);
};

export interface GetOperatorDropdownListParams {
  workstation_group_id?: number;
  time_range_type?: TimeRangeType;
}

export interface GetOperatorDropdownListResponse {
  data?: {
    list?: {
      ops_id?: string;
      ops_name?: string;
    }[];
  };
  message?: string;
  retcode?: number;
}

export const getOperatorDropdownList = (params: GetOperatorDropdownListParams) => {
  return request.post<GetOperatorDropdownListResponse>('/api/wfm/admin/workstation/dashboard/dashboard_operators_dropdown', params);
};

export interface GetOperatorPerformanceLastTenHoursParams {
  biz_workstation_id_list?: string[];
  count?: number;
  operator_id_list?: string[];
  /**
   * 0: asc, 1: desc
   */
  order_by_sequence?: number;
  /**
   * 按照哪一列时间排序, 从0开始
   */
  order_by_time_index?: number;
  pageno?: number;
  workstation_group_id: number;
}

export interface GetOperatorPerformanceLastTenHoursResponse {
  data?: {
    count?: number;
    items?: {
      hour_items?: {
        target?: number;
        throughput?: number;
        time_begin?: number;
        time_end?: number;
      }[];
      operator?: string;
      workstation?: string;
    }[];
    pageno?: number;
    total?: number;
  };
  message?: string;
  retcode?: number;
}

export const getOperatorPerformanceLastTenHours = (params: GetOperatorPerformanceLastTenHoursParams) => {
  return request.post<GetOperatorPerformanceLastTenHoursResponse>('/api/wfm/admin/workstation/dashboard/dashboard_operator_last_ten_hours', params);
};

export interface GetTopOperatorsParams {
  top_count?: number;
  workstation_group_id?: number;
}

export interface GetTopOperatorsResponse {
  data?: {
    items?: {
      operator?: string;
      target?: number;
      throughput?: number;
      workstation?: string;
    }[];
  };
  message?: string;
  retcode?: number;
}

export const getTopOperators = (params: GetTopOperatorsParams) => {
  return request.post<GetTopOperatorsResponse>('/api/wfm/admin/workstation/dashboard/dashboard_top_operators', params);
};

export interface GetWorkstationPerformanceLastTenHoursParams {
  workstation_group_id: number;
  biz_workstation_id_list?: string[];
}

export interface GetWorkstationPerformanceLastTenHoursResponse {
  retcode?: number;
  message?: string;
  data?: {
    target_items?: {
      last_hour_compare_rate?: number;
      on_target_rate?: number;
      time_begin?: number;
      time_end?: number;
      workstation?: string;
    }[];
    throughput_items?: {
      target?: number;
      throughput?: number;
      time_begin?: number;
      time_end?: number;
      workstation?: string;
    }[];
    total?: number;
  };
}

export const getWorkstationPerformanceLastTenHours = (params: GetWorkstationPerformanceLastTenHoursParams) => {
  return request.post<GetWorkstationPerformanceLastTenHoursResponse>('/api/wfm/admin/workstation/dashboard/dashboard_workstation_last_ten_hours', params);
};
